// src/utils/dentallyAppointmentSync.js
//
// One place that knows how a CRM appointment edit/reschedule relates to its
// Dentally booking: whether a provider write will happen, and what the
// backend's answer means once it comes back.
//
// WHY A SINGLE MODULE
// ------------------------------------------------------------------
// Reschedule (RescheduleAppointment.jsx), edit (EditAppointment.jsx) and the
// UpdateAppointment thunk all need the same answers, and the partial-success
// case must read identically in all of them. Spreading the response shape
// across those files is how "CRM saved, Dentally did not" becomes a generic
// error in one surface and a success toast in another.
//
// BACKEND CONTRACT  (apps/dentally/appointment_update.py)
// ------------------------------------------------------------------
// Two requests, in this order, for a LINKED appointment:
//
//   1. PUT  .../leads/{lead}/appointments/{id}/        — the full CRM field save.
//      It saves and stops. It makes NO provider call.
//   2. POST .../leads/{lead}/appointments/{id}/reschedule-with-dentally/
//      body { datetime?, note?, expected_dentally_appointment_id? }
//      This is the ONE AND ONLY write path to Dentally for an edit, and it
//      also reports the outcome. There is deliberately no second path: one
//      user action reaching a live practice management system through two
//      independent routes is not something an idempotency key should have to
//      defend. It answers `synced` on the write, and `already_synced` only
//      when genuinely re-delivered — a retry, a double submit, or saving the
//      same time twice.
//
// Because step 2 is the writer, it is fired whenever the appointment is linked,
// whether or not the caller intends to show its result. Skipping it leaves
// Dentally stale.
//
// The CRM write is authoritative and is never rolled back by a provider
// failure. That is the whole reason this module exists: every non-success
// outcome below still means the CRM saved.
//
//   { outcome, retryable, code?, appointment?, dentally?, retry_after_seconds? }
//
// outcome ∈ synced | already_synced | crm_only | skipped | in_progress
//           | dentally_unsynced | reconciliation_required | rejected
//
// `dentally` is DentallyAppointmentLink.safe_summary():
//   { appointment_id, creation_state, cancellation_state,
//     update_state, update_unsynced, reconciliation_required }
//
// An unlinked appointment never reaches step 2 at all, so it keeps today's
// behaviour exactly: one request, the old success message, no Dentally text.
//
// An outcome this module has never seen resolves to "needs review", never to
// success. A new provider state must not be announced as "updated in Dentally".

export const APPOINTMENT_SYNC_KIND = {
  /** No Dentally link, or nothing to propagate. Behaves as a CRM-only appointment. */
  crmOnly: 'crm_only',
  /** CRM and Dentally both hold the new time. */
  synced: 'synced',
  /** CRM updated; the Dentally write is still running. */
  pending: 'pending',
  /** CRM updated; Dentally was not and still shows the previous booking. */
  providerFailed: 'provider_failed',
  /** CRM updated; the Dentally outcome is unknown and needs review before rebooking. */
  providerUnknown: 'provider_unknown',
};

/** Backend `outcome` → the kind we present. */
const OUTCOME_KINDS = {
  synced: APPOINTMENT_SYNC_KIND.synced,
  already_synced: APPOINTMENT_SYNC_KIND.synced,
  crm_only: APPOINTMENT_SYNC_KIND.crmOnly,
  skipped: APPOINTMENT_SYNC_KIND.crmOnly,
  in_progress: APPOINTMENT_SYNC_KIND.pending,
  dentally_unsynced: APPOINTMENT_SYNC_KIND.providerFailed,
  rejected: APPOINTMENT_SYNC_KIND.providerFailed,
  reconciliation_required: APPOINTMENT_SYNC_KIND.providerUnknown,
};

/**
 * What a save will do to Dentally, from the appointment's link summary alone.
 *
 *   none        – nothing to sync (no link, or already cancelled in Dentally).
 *   willSync    – a confirmed Dentally booking that the save will move.
 *   unconfirmed – a link exists but its Dentally booking is not confirmed, so
 *                 the save cannot safely move it. The user is told the CRM
 *                 changes and Dentally does not, rather than being promised a
 *                 sync the backend will refuse.
 */
export const APPOINTMENT_SYNC_INTENT = {
  none: 'none',
  willSync: 'will_sync',
  unconfirmed: 'unconfirmed',
  /**
   * A previous propagation attempt's outcome could not be proven. The backend
   * refuses this with a 409 and never retries it automatically — the lead-review
   * reconciliation wizard owns resolving it. It gets its own intent because
   * "not confirmed yet" and "parked, a human must check Dentally" are different
   * things to tell someone, and silently skipping it would hide the second.
   */
  needsReview: 'needs_review',
};

const hasDentallyId = (dentally) => (
  dentally?.appointment_id != null && dentally.appointment_id !== ''
);

const isSummary = (value) => Boolean(
  value && typeof value === 'object' && !Array.isArray(value),
);

/** True when this appointment carries a Dentally link record of any kind. */
export const isDentallyLinkedAppointment = (appointment) => isSummary(appointment?.dentally);

export const getAppointmentSyncIntent = (dentally) => {
  if (!isSummary(dentally)) return APPOINTMENT_SYNC_INTENT.none;
  // A cancelled provider booking is not resurrected by an edit — the backend
  // refuses with dentally_cancellation_conflict rather than rebooking it.
  if (dentally.cancellation_state === 'CANCELLED') return APPOINTMENT_SYNC_INTENT.none;
  if (dentally.update_state === 'UNKNOWN') return APPOINTMENT_SYNC_INTENT.needsReview;
  if (dentally.reconciliation_required === true) return APPOINTMENT_SYNC_INTENT.needsReview;
  if (dentally.creation_state === 'UNKNOWN' || dentally.cancellation_state === 'UNKNOWN') {
    return APPOINTMENT_SYNC_INTENT.needsReview;
  }
  // creation_state ACTIVE + an id is what the backend's claim step requires.
  if (dentally.creation_state !== 'ACTIVE' || !hasDentallyId(dentally)) {
    return APPOINTMENT_SYNC_INTENT.unconfirmed;
  }
  // A cancel in flight loses to the same conflict check.
  if (dentally.cancellation_state && dentally.cancellation_state !== 'ACTIVE') {
    return APPOINTMENT_SYNC_INTENT.unconfirmed;
  }
  return APPOINTMENT_SYNC_INTENT.willSync;
};

/**
 * True when the appointment should take the durable reschedule path, i.e. the
 * second request is worth making. Mirrors the backend's own precondition, so a
 * CRM-only appointment issues exactly one request as it always has.
 */
export const shouldConfirmDentallySync = (dentally) => (
  getAppointmentSyncIntent(dentally) === APPOINTMENT_SYNC_INTENT.willSync
);

// ── Wording ──────────────────────────────────────────────────────────────────
// The partial-success sentence always states both halves in the same order:
// what the CRM did, then what Dentally did not do, then what to do about it.
// The lead-review reconciliation wizard surfaces this same divergence (it reads
// the link's `update_unsynced`), so these strings are its shared reference.

export const APPOINTMENT_SYNC_NOTICE = {
  willSync: {
    title: 'This change will also update Dentally',
    body: 'Saving moves the booking in Dentally as well as in the CRM.',
    tone: 'success',
    icon: 'mdi:calendar-sync-outline',
  },
  unconfirmed: {
    title: 'Dentally will not be changed',
    body: 'This appointment’s Dentally booking is not confirmed, so saving updates the CRM only. Check Dentally before rebooking.',
    tone: 'warning',
    icon: 'mdi:alert-circle-outline',
  },
  needsReview: {
    title: 'The Dentally record needs checking',
    body: 'A previous update to this booking did not confirm, so Dentally may not match the CRM. Saving updates the CRM only. Check Dentally before rebooking — do not assume the old time still stands.',
    tone: 'warning',
    icon: 'mdi:alert-circle-outline',
  },
};

const SYNC_MESSAGES = {
  [APPOINTMENT_SYNC_KIND.synced]: 'Appointment updated in the CRM and in Dentally',
  [APPOINTMENT_SYNC_KIND.pending]: 'Appointment updated in the CRM. The Dentally update is still in progress — refresh the appointment to see whether it completed.',
  [APPOINTMENT_SYNC_KIND.providerFailed]: 'Appointment updated in the CRM only. Dentally was not updated and still shows the previous booking.',
  [APPOINTMENT_SYNC_KIND.providerUnknown]: 'Appointment updated in the CRM. The Dentally outcome is unknown and needs review — check Dentally before rebooking, and do not assume the old time still stands.',
};

/** What to do next, per backend `code`. Keeps the raw code off the screen. */
const CODE_ADVICE = {
  // §5 date lock (backend PR #7027): an appointment with an outcome recorded
  // (attended / failed to attend / cancelled) keeps its date; a new visit is a
  // new appointment.
  appointment_date_locked: 'This appointment already has an outcome recorded, so its date cannot change. Book a new appointment for the new date.',
  dentally_unavailable: 'Dentally could not be reached. Try saving again in a few minutes.',
  dentally_request_rejected: 'Dentally rejected the new time. Change it in Dentally directly.',
  dentally_integration_inactive: 'This practice’s Dentally integration is not active.',
  dentally_cancellation_conflict: 'The Dentally booking is being cancelled, so the new time was not sent.',
  dentally_appointment_creation_unresolved: 'The Dentally booking for this appointment was never confirmed.',
  dentally_appointment_practice_mismatch: 'The Dentally booking on file belongs to another practice.',
  dentally_appointment_identity_mismatch: 'The Dentally booking on file is not the one shown here. Reload before trying again.',
  dentally_appointment_cancelled_remotely: 'This booking has been cancelled in Dentally.',
  dentally_duration_unresolved: 'The booked length could not be read from Dentally, so the new time was not sent.',
  dentally_update_attempt_interrupted: 'A previous Dentally update was interrupted and its result is unknown.',
  dentally_update_outcome_unknown: 'Dentally did not confirm the change.',
  appointment_not_reschedulable: 'Only a booked appointment can be moved in Dentally.',
};

// Snackbar severity. A provider failure is never `error`: the CRM write
// succeeded, and an error toast would tell the user nothing saved.
//
// Only 'success' and 'warning' appear here on purpose. ErrorHandler (the global
// snackbar that renders updateError) recognises `severity: 'warning'` and a
// truthy `success`; anything else falls through to the error variant AND is
// captured to Sentry as a UI Popup Error. 'info' would therefore paint a red
// toast and raise a false alarm, so the still-in-progress case is a warning.
const SYNC_VARIANTS = {
  [APPOINTMENT_SYNC_KIND.synced]: 'success',
  [APPOINTMENT_SYNC_KIND.pending]: 'warning',
  [APPOINTMENT_SYNC_KIND.providerFailed]: 'warning',
  [APPOINTMENT_SYNC_KIND.providerUnknown]: 'warning',
};

/** Long enough to read the two-clause partial-success sentence. */
export const SYNC_WARNING_TIMEOUT_MS = 12000;

/**
 * Normalise a reschedule-with-dentally response into one outcome.
 *
 * Returns null when there is no Dentally story to tell — the response carries
 * no outcome, or the outcome is crm_only / skipped. Null is the signal to
 * callers that nothing about their existing behaviour changes.
 */
export const resolveAppointmentSyncOutcome = (response) => {
  const data = response?.data;
  const outcome = typeof data?.outcome === 'string' ? data.outcome : null;
  if (!outcome) return null;

  const kind = OUTCOME_KINDS[outcome] || APPOINTMENT_SYNC_KIND.providerUnknown;
  if (kind === APPOINTMENT_SYNC_KIND.crmOnly) return null;

  const code = typeof data.code === 'string' && data.code ? data.code : null;
  const advice = code ? CODE_ADVICE[code] || null : null;
  const base = SYNC_MESSAGES[kind];

  return {
    kind,
    outcome,
    code,
    variant: SYNC_VARIANTS[kind],
    // The advice is appended, never substituted: the CRM-saved half of the
    // sentence must survive whatever the provider reason happens to be.
    message: advice && kind !== APPOINTMENT_SYNC_KIND.synced ? `${base} ${advice}` : base,
    advice,
    retryable: data.retryable === true,
    retryAfterSeconds: Number.isFinite(data.retry_after_seconds)
      ? data.retry_after_seconds
      : null,
    reconciliationRequired: data.dentally?.reconciliation_required === true
      || kind === APPOINTMENT_SYNC_KIND.providerUnknown,
    dentally: isSummary(data.dentally) ? data.dentally : null,
  };
};

/**
 * The `updateError` payload for an outcome, so the reschedule and edit paths
 * cannot drift in severity or in how long the message stays on screen.
 */
export const buildAppointmentSyncToast = (outcome) => {
  if (!outcome) return null;
  if (outcome.variant === 'success') {
    return { success: true, message: outcome.message };
  }
  return {
    severity: 'warning',
    message: outcome.message,
    timeout: SYNC_WARNING_TIMEOUT_MS,
  };
};

/**
 * The datetime to send to the reschedule endpoint: the caller's string,
 * VERBATIM.
 *
 * DO NOT REFORMAT THE VALUE HERE. An earlier version of this helper rewrote
 * `DD-MM-YYYY HH:mm:ss.0Z` into `YYYY-MM-DDTHH:MM:SSZ` on the theory that the
 * endpoint took ISO only. It does not, and the rewrite was itself the bug:
 *
 *   REST_FRAMEWORK.DATETIME_INPUT_FORMATS is ["%d-%m-%Y %H:%M:%S.%fZ", "iso-8601"]
 *   and TIME_ZONE is Europe/London with USE_TZ on.
 *
 * In the first format the trailing Z is a literal character, not a zone, so the
 * legacy string parses NAIVE and is then localised to Europe/London. An ISO
 * string with a real Z parses as UTC. Through British Summer Time those are an
 * hour apart. So a reformatted POST would have described a different instant
 * from the PUT that carried the same digits, and the backend — seeing a target
 * it had not just stored — would have moved the appointment a second time and
 * pushed the wrong hour to Dentally. Silently, and only from late March to late
 * October.
 *
 * Both requests of one edit must carry the IDENTICAL string. That is the whole
 * invariant: whatever it means, it means the same thing to both endpoints. The
 * backend pins it from its side in
 * test_both_endpoints_read_one_datetime_string_as_one_instant.
 *
 * This function therefore only decides whether to send at all. It returns the
 * input untouched when it is a datetime the CRM editor could have produced, and
 * null otherwise, so a missing or malformed value skips the provider call
 * instead of reaching the endpoint as a guess.
 */
const LEGACY_DATETIME = /^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export const appointmentDatetimeForSync = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!LEGACY_DATETIME.test(trimmed) && !ISO_DATETIME.test(trimmed)) return null;
  // Verbatim. See above.
  return value;
};
